"use client";
import { Flex, Box, Text, Grid } from "@chakra-ui/react";
import { ImageFragment } from "lib/gcms/gcms-typings";
import { useEffect, useState, useCallback } from "react";
import Image from "next/image";

export interface IconCarouselItem {
  id: string;
  label?: string;
  icon?: ImageFragment & { alt?: string };
}

export interface IconCarouselProps {
  items: IconCarouselItem[];
  /** ms between auto-advances; pass 0 to disable */
  interval?: number;
  activeIndex?: number;
  onActiveChange?: (index: number) => void;
}

export const IconCarousel = ({
  items,
  interval = 4000,
  activeIndex,
  onActiveChange,
}: IconCarouselProps) => {
  const [internal, setInternal] = useState(0);
  const active = activeIndex ?? internal;
  const [paused, setPaused] = useState(false);

  const setActive = useCallback(
    (next: number) => {
      if (onActiveChange) onActiveChange(next);
      else setInternal(next);
    },
    [onActiveChange],
  );

  useEffect(() => {
    if (!interval || paused) return;
    const id = setInterval(() => {
      setActive((active + 1) % items.length);
    }, interval);
    return () => clearInterval(id);
  }, [interval, paused, items.length, active, setActive]);

  return (
    <Grid
      templateColumns={{
        base: "repeat(2, 1fr)",
        lg: "repeat(5, 1fr)",
      }}
      role="tablist"
      gap={5}
      justifyItems="center"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {items.map((item, i) => {
        const isActive = i === active;
        return (
          <Flex
            key={item.id}
            as="button"
            role="tab"
            aria-selected={isActive}
            aria-label={item.label}
            onClick={() => setActive(i)}
            onFocus={() => setPaused(true)}
            onBlur={() => setPaused(false)}
            flexDir="column"
            alignItems="center"
            justifyContent="center"
            gap={2}
            w={{ base: "120px", md: "200px" }}
            flexShrink={0}
            py={{ base: 5, md: 8 }}
            borderRadius="16px"
            bgColor={isActive ? "white" : "transparent"}
            boxShadow={isActive ? "0 4px 20px rgba(0,0,0,0.12)" : "none"}
            transform={isActive ? "translateY(-4px)" : "none"}
            transition="background-color 0.3s, box-shadow 0.3s, transform 0.3s"
            _hover={{ transform: "translateY(-4px)" }}
            _focusVisible={{ outline: "2px solid", outlineColor: "blue.400" }}
          >
            {item.icon && (
              <Box
                position="relative"
                boxSize={{ base: 14, md: 16, lg: 20 }}
                opacity={isActive ? 1 : 0.6}
                transition="opacity 0.3s"
              >
                <Image
                  src={item.icon.url}
                  alt={item.icon?.alt || ""}
                  sizes="32px"
                  style={{ objectFit: "contain" }}
                  height={100}
                  width={100}
                />
              </Box>
            )}
            <Text
              fontSize={{ base: 14, md: 16, lg: 18 }}
              fontWeight="bold"
              opacity={isActive ? 1 : 0.6}
              color={"black"}
            >
              {item.label}
            </Text>
          </Flex>
        );
      })}
    </Grid>
  );
};
